import { supabase } from "@/lib/supabaseClient";

export type AccountNetwork = "instagram" | "facebook" | "tiktok";
export const MAX_ACCOUNTS_PER_NETWORK = 3;

export type ConnectedAccount = {
  id: string;
  network: AccountNetwork;
  handle: string;
  profileUrl: string;
  label: string | null;
  active: boolean;
  lastScrapedAt: string | null;
  lastResult: { posts?: number; commenters?: number; inserted?: number; message?: string };
};

export const NETWORK_META: Record<AccountNetwork, { label: string; icon: string; placeholder: string }> = {
  instagram: { label: "Instagram", icon: "ri-instagram-line", placeholder: "@tuusuario o link de tu perfil" },
  facebook: { label: "Facebook", icon: "ri-facebook-circle-line", placeholder: "link de tu página o perfil público" },
  tiktok: { label: "TikTok", icon: "ri-tiktok-line", placeholder: "@tuusuario o link de tu perfil" },
};

/** Convierte "@usuario", "usuario" o un link en { handle, profileUrl }. */
export function parseAccountInput(network: AccountNetwork, raw: string): { handle: string; profileUrl: string } | null {
  const text = raw.trim();
  if (!text) return null;
  let handle = text;
  let url: URL | null = null;
  if (/^https?:\/\//i.test(text) || /^(www\.)?(instagram|facebook|fb|tiktok)\.com/i.test(text)) {
    try {
      url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
    } catch {
      return null;
    }
    const host = url.hostname.replace(/^www\./, "").replace(/^m\./, "");
    const ok = network === "instagram" ? host === "instagram.com" : network === "facebook" ? host === "facebook.com" || host === "fb.com" : host === "tiktok.com";
    if (!ok) return null;
    if (network === "facebook" && url.pathname.startsWith("/profile.php")) handle = url.searchParams.get("id") ?? "";
    else handle = url.pathname.split("/").filter(Boolean)[0] ?? "";
  }
  handle = handle.replace(/^@/, "").trim();
  if (!/^[A-Za-z0-9._-]{2,60}$/.test(handle)) return null;
  const h = handle.toLowerCase();
  const profileUrl =
    network === "instagram"
      ? `https://www.instagram.com/${h}/`
      : network === "tiktok"
        ? `https://www.tiktok.com/@${h}`
        : /^\d+$/.test(h)
          ? `https://www.facebook.com/profile.php?id=${h}`
          : `https://www.facebook.com/${handle}`;
  return { handle: h, profileUrl };
}

type Row = {
  id: string;
  network: AccountNetwork;
  handle: string;
  profile_url: string;
  label: string | null;
  active: boolean;
  last_scraped_at: string | null;
  last_result: ConnectedAccount["lastResult"] | null;
};

const toAccount = (r: Row): ConnectedAccount => ({
  id: r.id,
  network: r.network,
  handle: r.handle,
  profileUrl: r.profile_url,
  label: r.label,
  active: r.active,
  lastScrapedAt: r.last_scraped_at,
  lastResult: r.last_result ?? {},
});

export async function fetchConnectedAccounts(): Promise<ConnectedAccount[]> {
  const { data, error } = await supabase
    .from("connected_accounts")
    .select("id, network, handle, profile_url, label, active, last_scraped_at, last_result")
    .order("created_at");
  if (error) throw error;
  return (data as Row[]).map(toAccount);
}

export async function addConnectedAccount(network: AccountNetwork, input: string, label?: string): Promise<void> {
  const parsed = parseAccountInput(network, input);
  if (!parsed) throw new Error(`Revisa el usuario o el link de ${NETWORK_META[network].label}.`);
  const { error } = await supabase.from("connected_accounts").insert({ network, handle: parsed.handle, profile_url: parsed.profileUrl, label: label?.trim() || null });
  if (error) {
    if (error.message.includes("max_accounts_reached")) throw new Error(`Ya conectaste ${MAX_ACCOUNTS_PER_NETWORK} cuentas de ${NETWORK_META[network].label}.`);
    if (error.code === "23505") throw new Error("Esa cuenta ya está conectada.");
    throw new Error("No se pudo conectar la cuenta. Intenta de nuevo.");
  }
}

export async function removeConnectedAccount(id: string): Promise<void> {
  const { error } = await supabase.from("connected_accounts").delete().eq("id", id);
  if (error) throw error;
}

export async function setConnectedAccountActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from("connected_accounts").update({ active }).eq("id", id);
  if (error) throw error;
}

export type AccountScrapeResult = { inserted: number; posts?: number; commenters?: number; duplicates?: number; spam?: number; message?: string };

const SCRAPE_ERRORS: Record<string, string> = {
  too_soon: "Esta cuenta se analizó hace poco. Espera unos minutos para volver a buscar.",
  apify_not_configured: "El buscador todavía no está activado. Avísale a tu administrador.",
  apify_timeout: "La búsqueda tardó demasiado. Inténtalo de nuevo en unos minutos.",
  account_inactive: "Activa la cuenta para poder buscar con ella.",
  account_not_found: "No encontramos esa cuenta.",
};

/** Lee las publicaciones públicas recientes de la cuenta y guarda como prospectos a quienes comentaron. */
export async function scrapeFromAccount(accountId: string): Promise<AccountScrapeResult> {
  const { data, error } = await supabase.functions.invoke("apify-prospects", { body: { mode: "accounts", account_id: accountId } });
  if (error) {
    let code = "";
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      const body = await ctx.clone().json().catch(() => null);
      code = body?.error ?? "";
    }
    throw new Error(SCRAPE_ERRORS[code] ?? "No se pudo completar la búsqueda. Inténtalo de nuevo.");
  }
  return (data ?? {}) as AccountScrapeResult;
}

export type ProfileCandidate = { handle: string; name: string | null; photo: string | null; followers: number | null; verified: boolean; url: string };

const SEARCH_ERRORS: Record<string, string> = {
  query_too_short: "Escribe al menos 2 letras.",
  apify_not_configured: "El buscador todavía no está activado. Avísale a tu administrador.",
  apify_timeout: "La búsqueda tardó demasiado. Inténtalo de nuevo.",
  search_failed: "No pudimos buscar en esta red ahora. Pega el link de tu perfil para conectarla.",
};

/** Busca perfiles públicos por nombre para que el socio elija el suyo. */
export async function searchProfiles(network: AccountNetwork, q: string): Promise<ProfileCandidate[]> {
  const { data, error } = await supabase.functions.invoke("apify-profile-search", { body: { network, q } });
  if (error) {
    let code = "";
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      const body = await ctx.clone().json().catch(() => null);
      code = body?.error ?? "";
    }
    throw new Error(SEARCH_ERRORS[code] ?? "No se pudo buscar. Inténtalo de nuevo.");
  }
  return ((data as { results?: ProfileCandidate[] })?.results ?? []).slice(0, 6);
}
